import { Action } from 'redux';

export enum EventsActionType {
  /* All events from our db */
  FETCH_EVENTS = 'FETCH_EVENTS',
  FETCH_EVENTS_SUCCESS = 'FETCH_EVENTS_SUCCESS',
  FETCH_EVENTS_ERROR = 'FETCH_EVENTS_ERROR',

  /* Event properties */
  FETCH_EVENT_PROPERTIES = 'FETCH_EVENT_PROPERTIES',
  FETCH_EVENT_PROPERTIES_SUCCESS = 'FETCH_EVENT_PROPERTIES_SUCCESS',
  FETCH_EVENT_PROPERTIES_ERROR = 'FETCH_EVENT_PROPERTIES_ERROR',

  /* Event details */
  FETCH_EVENT_DETAILS = 'FETCH_EVENT_DETAILS',
  FETCH_EVENT_DETAILS_SUCCESS = 'FETCH_EVENT_DETAILS_SUCCESS',
  FETCH_EVENT_DETAILS_ERROR = 'FETCH_EVENT_ERROR',

  /* Available livestorm event titles */
  FETCH_EVENT_TITLES = 'FETCH_EVENT_TITLES',
  FETCH_EVENT_TITLES_SUCCESS = 'FETCH_EVENT_TITLES_SUCCESS',
  FETCH_EVENT_TITLES_ERROR = 'FETCH_EVENT_TITLES_ERROR',

  /* Sessions for given event */
  FETCH_EVENT_SESSIONS = 'FETCH_EVENT_SESSIONS',
  FETCH_EVENT_SESSIONS_SUCCESS = 'FETCH_EVENT_SESSIONS_SUCCESS',
  FETCH_EVENT_SESSIONS_ERROR = 'FETCH_EVENT_SESSIONS_ERROR',

  /* Session details */
  FETCH_SESSION_DETAILS = 'FETCH_SESSION_DETAILS',
  FETCH_SESSION_DETAILS_SUCCESS = 'FETCH_SESSION_DETAILS_SUCCESS',
  FETCH_SESSION_DETAILS_ERROR = 'FETCH_SESSION_DETAILS_ERROR',

  /* Filters and sorting */
  SET_EVENTS_CATEGORY_FILTER = 'SET_EVENTS_CATEGORY_FILTER',
  SET_EVENTS_TIMELINE_FILTER = 'SET_EVENTS_TIMELINE_FILTER',
  SET_EVENTS_FAVORITES_FILTER = 'SET_EVENTS_FAVORITES_FILTER',
  SET_EVENTS_SORT_DESC = 'SET_EVENTS_SORT_DESC',
}

export type EventsAction = Action<EventsActionType> & {
  payload: any;
};

export type EventSession = {
  id: string;
  dateTime: Date;
};

export type EventCategory = 'CREDIT_ANALYSIS' | 'FUNDS_ANALYSIS' | 'ESG_ANALYSIS';

export type Document = {
  id: string;
  entityId: string;
  title: string;
};

export type Event = {
  id?: string;
  status: 'ACTIVE' | 'DRAFT';
  externalId: string;
  type: string;
  image: File | string;
  title: string;
  shortDescription: string;
  description: string;
  category: EventCategory;
  region: string;
  industry: string;
  eventDate: Date;
  duration: number;
  languages?: 'de' | 'en';
  organiser: string;
  thirdPartySource: boolean;
  segment: string;
  productLine: string;
  marketingLabel: string;
  location: {
    city?: string;
    name: string;
  };
  documentList?: File[] | Document[];
  userRegistered?: boolean;
  favourite?: boolean;
  connectionLink?: string;
};

export type EventFilters = {
  category: EventCategory | '';
  timeline: string;
  favourites: boolean;
};

export type Response<T = any> = {
  isPending: boolean;
  data: T;
};

export type EventsStore = {
  events: Response<any[]>;
  properties: Response<Record<string, SelectOption[]>>;
  filters: EventFilters;
  sortDesc: boolean;
  eventsDetails: Record<string, Response<Event>>;
  eventTitles: Response<
    {
      id: string;
      attributes: {
        title: string;
      };
    }[]
  >;
  eventSessions: Record<string, Response<EventSession[]>>;
  sessionsDetails: Record<string, Response>;
};

export type SelectOption = {
  name: string;
  id: string;
};

export type FieldControlProps = {
  name: string;
  type?:
    | 'text'
    | 'number'
    | 'select'
    | 'date-time'
    | 'autocomplete'
    | 'rich-text'
    | 'checkbox'
    | 'image'
    | 'documents';
  label: string;
  options?: SelectOption[] | null;
  disabled?: boolean;
  error?: unknown;
  value?: unknown;
  onChange: (name: string, value: unknown) => void;
  /* Tracks control update, that is not yet a value change (eg. input text on autocomplete) */
  onUpdate?: (name: string, value: unknown) => void;
};

export type FieldDefinition = Omit<FieldControlProps, 'onChange' | 'onUpdate' | 'disabled'> & {
  width?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
};

export type GroupDefinition = {
  name: string;
  width?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  type: 'group';
  controls: FieldDefinition[];
};

export type SchemaElement = FieldDefinition | GroupDefinition;
